import { useState, useCallback } from 'react';
import { supabaseData } from '../../utils/supabase-data-client';
import { refetchAndCache } from './refetchAndCache';
import { useCacheStore } from './useCacheStore';
import { MutationResult } from './useCategoryMutations';

export type NewBookmarkInput = {
  bookmark_name: string;
  bookmark_url: string;
  bookmark_notes: string;
  category_id: string | null;
};

export type BookmarkUpdateInput = {
  bookmark_name: string;
  bookmark_url: string;
  bookmark_notes: string;
};

interface UseBookmarkMutationsReturn {
  addBookmark: (input: NewBookmarkInput) => Promise<MutationResult>;
  updateBookmark: (
    bookmarkId: string,
    updates: BookmarkUpdateInput
  ) => Promise<MutationResult>;
  deleteBookmark: (bookmarkId: string) => Promise<MutationResult>;
  moveBookmark: (
    bookmarkId: string,
    newCategoryId: string | null
  ) => Promise<MutationResult>;
  loading: boolean;
}

function validateBookmarkName(name: string): {
  valid: boolean;
  trimmed: string;
} {
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > 255) {
    return { valid: false, trimmed };
  }
  return { valid: true, trimmed };
}

export const useBookmarkMutations = (): UseBookmarkMutationsReturn => {
  const [loading, setLoading] = useState(false);
  const cacheStore = useCacheStore();

  const addBookmark = useCallback(
    async (input: NewBookmarkInput): Promise<MutationResult> => {
      const { valid, trimmed } = validateBookmarkName(input.bookmark_name);
      if (!valid) {
        return {
          success: false,
          error: 'Bookmark name must be between 1 and 255 characters.',
        };
      }

      setLoading(true);
      try {
        const { error } = await supabaseData.from('bookmarks').insert({
          bookmark_name: trimmed,
          bookmark_url: input.bookmark_url,
          bookmark_notes: input.bookmark_notes,
          category_id: input.category_id,
        });

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('bookmarks', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  const updateBookmark = useCallback(
    async (
      bookmarkId: string,
      updates: BookmarkUpdateInput
    ): Promise<MutationResult> => {
      const { valid, trimmed } = validateBookmarkName(updates.bookmark_name);
      if (!valid) {
        return {
          success: false,
          error: 'Bookmark name must be between 1 and 255 characters.',
        };
      }

      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('bookmarks')
          .update({
            bookmark_name: trimmed,
            bookmark_url: updates.bookmark_url,
            bookmark_notes: updates.bookmark_notes,
            last_modified: new Date().toISOString(),
          })
          .eq('bookmark_id', bookmarkId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('bookmarks', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  const deleteBookmark = useCallback(
    async (bookmarkId: string): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('bookmarks')
          .delete()
          .eq('bookmark_id', bookmarkId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('bookmarks', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  const moveBookmark = useCallback(
    async (
      bookmarkId: string,
      newCategoryId: string | null
    ): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('bookmarks')
          .update({ category_id: newCategoryId })
          .eq('bookmark_id', bookmarkId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('bookmarks', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  return {
    addBookmark,
    updateBookmark,
    deleteBookmark,
    moveBookmark,
    loading,
  };
};
